<template>
  <qs-drawer
    :visible.sync="visible"
    cusSize="xlg"
    cusType="group"
    title="车型详情"
    :before-close="close">
    <div class="content-container">
      <div class="left-nav">
        <div
          @click="goAnchor(index, item)"
          :class="activeIndex === index ? 'nav-item active' : 'nav-item'"
          v-for="(item, index) in navList" :key="index">
            <div v-if="activeIndex === index" class='active-line'></div>
            {{ item.text }}
        </div>
      </div>
      <div class="right-container" ref="scrollView" @scroll="handleScroll">
        <card title="基本参数">
          <column-box
            :column="3"
            :space="32"
            :source="form"
            :list="baseInfoColumns"></column-box>
        </card>
        <card title="车型外观图">
          <div class="image-list">
            <div
              v-for="(item, index) in carAppearanceColumns" :key="index"
              class="img-item">
              <label class="name">{{ item.name }}</label>
              <el-image
                v-if="form[item.key]"
                class="image"
                :key="index"
                :src="form[item.key]"
                :preview-src-list="[form[item.key]]"
              ></el-image>
              <span v-else>--</span>
            </div>
          </div>
        </card>
        <card title="车型内饰图">
          <div class="image-list">
            <div
              v-for="(item, index) in carInnerColumns" :key="index"
              class="img-item">
              <label class="name">{{ item.name }}</label>
              <el-image
                v-if="form[item.key]"
                class="image"
                :key="index"
                :src="form[item.key]"
                :preview-src-list="[form[item.key]]"
              ></el-image>
              <span v-else>--</span>
            </div>
          </div>
        </card>
        <card title="车型空间图">
          <div class="image-list">
            <div
              v-for="(item, index) in carSpaceColumns" :key="index"
              class="img-item">
              <label class="name">{{ item.name }}</label>
              <el-image
                v-if="form[item.key]"
                class="image"
                :key="index"
                :src="form[item.key]"
                :preview-src-list="[form[item.key]]"
              ></el-image>
              <span v-else>--</span>
            </div>
          </div>
        </card>
        <card title="电池" v-if="isElectricCar">
          <column-box
            :column="3"
            :space="32"
            :source="form"
            :list="electricInfoColumns"></column-box>
        </card>
        <card title="发动机" v-if="isGasolineCar">
          <column-box
            :column="3"
            :space="32"
            :source="form"
            :list="engineInfoColumns"></column-box>
        </card>
        <card title="车身参数">
          <column-box
            :column="3"
            :space="32"
            :source="form"
            :list="carInfoColumns"></column-box>
        </card>
        <card title="车载配置">
          <column-box
            :column="3"
            :space="32"
            :source="form"
            :list="inVehicleColumns"></column-box>
        </card>
      </div>
    </div>
  </qs-drawer>
</template>
<script setup>
import { ref, computed } from 'vue'
import {
  getModelDetail,
} from '@/api/carModel'
import { isNil } from 'lodash'

const visible = ref(false)
const form = ref({})
// 判断是否展示电池莫款
const isElectricCar = ref(false)
// 判断是否展示发动机模块
const isGasolineCar = ref(false)

const activeIndex = ref(0)
const scrollView = ref(null)
const isProgrammaticScroll = ref(false)
// 车型详情是否存在
const isInDetail = ref(false)

const formatterList = ({ key, name, unit = '', formatter }) => {
  if (formatter) {
    return {
      key,
      name,
      formatter,
    }
  }
  return {
    key,
    name,
    formatter: (value) => {
      if (!value) return '--'
      return `${value}${unit}`
    },
  }
}
// 基础信息配置
const baseInfoColumns = [
  { key: 'modelName', name: '车型名称' },
  {
    key: 'brandName',
    name: '品牌车系',
    formatter: (value) => {
      if (!value && !form.value.seriesName) return '--'
      return `${value}·${form.value.seriesName}`
    } },
  {
    key: 'modelYear',
    name: '车型年款',
    unit: '款',
  },
  { key: 'energyTypeName', name: '能源类型' },
  { key: 'operationTypeName', name: '运营类型' },
  {
    key: 'vehicleSize',
    name: '长 x 宽 x 高',
    unit: 'mm',
  },
].map(formatterList)
// 车型外观图配置
const carAppearanceColumns = [
  { key: 'leftFront45Pic', name: '左前45°角' },
  { key: 'frontSideLeftPic', name: '正侧面（左）' },
]
// 车型内饰图配置
const carInnerColumns = [
  { key: 'frontRowViewPic', name: '前排全景' },
  { key: 'steeringWheelPic', name: '主驾方向盘' },
  { key: 'centralControlPic', name: '中控' },
]
// 车型空间图配置
const carSpaceColumns = [
  { key: 'frontRowSidePic', name: '前排正侧' },
  { key: 'rearRowSidePic', name: '后排正侧' },
  { key: 'storageCompartmentPic', name: '后备箱' },
]
// 电池信息配置
const electricInfoColumns = [
  { key: 'pureElectricDrivingRange', name: '纯电续航里程', unit: 'km' },
  { key: 'fastCharging', name: '电池快充时间', unit: '小时' },
  { key: 'fastChargingRange', name: '电池快充电量范围', unit: '%' },
  { key: 'slowChargeChargingHour', name: '电池慢充时间', unit: '小时' },
  { key: 'batteryTypeName', name: '电池类型' },
  { key: 'batteryCoolingModeName', name: '电池冷却方式' },
].map(formatterList)
// 发动机配置
const engineInfoColumns = [
  { key: 'displacement', name: '排量', unit: 'L' },
  { key: 'inletModelName', name: '进气形式' },
  { key: 'rozName', name: '燃油标号' },
  { key: 'wltc', name: '综合油耗', unit: 'L/100km' },
].map(formatterList)

// 车身参数配置
const carInfoColumns = computed(() => {
  const list = [
    { key: 'wheelBase', name: '轴距', unit: 'mm' },
    { key: 'seats', name: '座位数', unit: '个' },
    { key: 'carDoorNum', name: '车门数', unit: '个' },
    { key: 'treadBefore', name: '前轮距', unit: 'mm' },
    { key: 'treadAfter', name: '后轮距', unit: 'mm' },
    { key: 'frontTire', name: '前轮胎规格' },
    { key: 'rearTire', name: '后轮胎规格' },
  ]
  if (isGasolineCar.value) {
    list.push({ key: 'fuelCapacity', name: '油箱容积', unit: 'L' })
  }
  list.push({ key: 'trunkCapacity', name: '后备箱容积', unit: 'L' })
  return list.map(formatterList)
})

// 车载配置
const inVehicleColumns = [
  {
    key: 'parkingRadarFront',
    name: '前驻车雷达',
    formatter: () => {
      if (isNil(form.value.parkingRadarFront)) return '--'
      return form.value.parkingRadarFront ? '有' : '无'
    },
  },
  {
    key: 'parkingRadarRear',
    name: '后驻车雷达',
    formatter: () => {
      if (isNil(form.value.parkingRadarRear)) return '--'
      return form.value.parkingRadarRear ? '有' : '无'
    },
  },
  { key: 'cameraBackUp',
    name: '驾驶辅助影像',
    formatter: () => {
      const { cameraBackUp, camera360, cameraSideBlind } = form.value
      const list = []
      if (cameraBackUp === true) list.push('倒车影像')
      if (camera360 === true) list.push('360度全景影像')
      if (cameraSideBlind === true) list.push('车侧盲区影像')
      return list.length > 0 ? list.join('、') : '--'
    } },
]

const emit = defineEmits(['refresh'])
const showDrawer = (row) => {
  visible.value = true
  getDetail(row)
}

const getDetail = async (row) => {
  isInDetail.value = false
  const { code, data } = await getModelDetail({
    modelId: row.id,
  })
  form.value = data || {}
  judgeCarType(data?.energyType)
  if (code === 1) {
    isInDetail.value = true
  }
}
const goAnchor = (index) => {
  const allCarContainer = document.querySelectorAll('.card-container')
  if (allCarContainer[index]) {
    isProgrammaticScroll.value = true
    scrollView.value.scrollTop = allCarContainer[index]?.offsetTop - 48
  }
  activeIndex.value = index
}

const handleScroll = () => {
  if (isProgrammaticScroll.value) {
    isProgrammaticScroll.value = false
    return
  }

  // 获取所有锚点元素
  const navContents = document.querySelectorAll('.card-container')
  // 所有锚点元素的 offsetTop
  const offsetTopArr = []
  navContents.forEach((item) => {
    offsetTopArr.push(item.offsetTop)
  })


  const scrollTop = scrollView.value?.scrollTop + 10
  offsetTopArr.forEach((item, index) => {
    if (scrollTop >= item) {
      activeIndex.value = index
    }
  })
}

const judgeCarType = (energyType) => {
  // 仅当能源类型为 汽油、插电式混动、增程式混动、油电混合、柴油、天然气 时,判断为油车
  isGasolineCar.value = [2, 3, 4, 5, 6, 7].includes(energyType)
  // 仅当能源类型为 纯电动、插电式混动、增程式混动 时，判断为电车
  isElectricCar.value = [1, 3, 4].includes(energyType)
}

const close = () => {
  if (!isInDetail.value) {
    emit('refresh')
  }
  visible.value = false
}
const navList = computed(() => {
  const list = [
    { text: '基本参数' },
    { text: '车型外观图' },
    { text: '车型内饰图' },
    { text: '车型空间图' },
  ]
  if (isElectricCar.value) {
    list.push({ text: '电池' })
  }
  if (isGasolineCar.value) {
    list.push({ text: '发动机' })
  }
  list.push({ text: '车身参数' })
  list.push({ text: '车载配置' })
  return list
})

defineExpose({
  showDrawer,
})

</script>
<style lang="scss" scoped>
::v-deep .custom-drawer-group.rtl .el-drawer__body {
  padding-right: 0;
  padding-bottom: 16px;
}
.content-container {
  display: flex;
  height: 100%;
  .left-nav {
    width: 200px;
    height: auto;
    border-radius: 12px;
    background: #FFFFFF;
    margin-right: 12px;
    margin-bottom: 16px;
    padding-top: 17px;
    .nav-item {
      height: 22px;
      margin-bottom: 10px;
      padding-left: 18px;
      cursor: pointer;
      line-height: 22px;
    }
    .active {
      color: #366CFE;
      font-weight: 500;
      position: relative;
      .active-line {
        width: 2px;
        height: 24px;
        background: #366CFE;
        border-radius: 2px;
        position: absolute;
        left: 0;
        top: -1px;
      }
    }
  }
  .right-container {
    flex: 1;
    height: 100%;
    overflow-y: scroll;
    padding-right: 16px;
    color: rgba(0, 0, 0, 0.6);
  }
}
.image-list {
  display: flex;
}
.img-item {
  display: flex;
  flex-direction: column;
  margin-right: 32px;
  margin-bottom: 12px;
  .name {
    color: #4E5969;
    font-size: 14px;
    line-height: 22px;
  }
  .image {
    width: 96px;
    height: auto;
    border-radius: 6px;
    margin-top: 8px;
  }
}
::v-deep .column-box {
  gap: 0 16px;
}
</style>
